import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import RelatorioEntregasService from "../../services/resource/RelatorioEntregasService";
import { useAuth } from "../../context/authContext";
import FilialService from "../../services/resource/filialService";
import { Loading } from "../../componentes/loading/loading";
import { ChartCard } from "../../componentes/chart/CardChart";
import { captureElementToImage } from "../../utils/captureElementToImage";
import { CustomMultiSelect } from "../../componentes/multiselect/CustomMultiSelect";
import { formatLocalDate, getHojeFormatado, getMesAtual } from "../../utils/formatDate";
import { toast } from "react-toastify";
import FileDownload from "js-file-download";
import { Dialog } from "primereact/dialog";
import ConferenciaFechamentoSolar from "./ConferenciaFechamentoSolar";

// ─── Paleta de cores (usada apenas em configs do ApexCharts) ──────────────────
const C = {
    teal: "#0d9488",
    tealMid: "#271de0",
    tealLight: "#5eead4",
    blue: "#2563eb",
    blueLight: "#93c5fd",
    rose: "#dd5555",
    roseLight: "#fda4af",
    amber: "#ff9900",
    violet: "#7c3aed",
    green: "#16a34a",
    orange: "#ea580c",
    slate: "#475569",
};

const PALETTE_DONUT = [
    C.rose, C.amber, C.violet, C.teal, C.blue,
    C.orange, C.green, C.roseLight, C.blueLight, C.tealLight,
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const n = (v) => (v != null ? Number(v) : 0);
const fmt = (v, d = 2) =>
    n(v).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtP = (v) => (v != null ? `${fmt(v)}%` : "—");
const fmtAbrev = (v) => {
    const valor = n(v);
    const abs = Math.abs(valor);
    const sinal = valor < 0 ? "-" : "";
    if (abs >= 1_000_000) return `${sinal}${fmt(abs / 1_000_000, 2)} Mi`;
    if (abs >= 1_000) return `${sinal}${fmt(abs / 1_000, 2)} Mil`;
    return `${sinal}${fmt(abs, 0)}`;
};

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const MESES_ABREV = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const mesAnoLabel = (item) => {
    const mes = n(item?.mes);
    const ano = n(item?.ano);
    const rotuloMes = MESES_ABREV[mes - 1] ?? String(mes ?? "");
    return ano ? `${rotuloMes}/${String(ano).slice(-2)}` : rotuloMes;
};

const mesDaData = (data) => {
    const encontrada = String(data ?? "").match(/^(\d{4})[-/](\d{2})/);
    if (!encontrada) return { chave: String(data ?? ""), label: String(data ?? "") };

    const [, ano, mes] = encontrada;
    return {
        chave: `${ano}-${mes}`,
        label: new Date(Number(ano), Number(mes) - 1, 1).toLocaleDateString("pt-BR", {
            month: "short",
            year: "numeric",
        }),
    };
};

// ─── Configs base ApexCharts ──────────────────────────────────────────────────
const baseChart = {
    toolbar: { show: false },
    animations: { enabled: false, speed: 500 },
};
const baseTip = { style: { fontSize: "11px" } };
const baseGrid = { borderColor: "#e0e0e0" };
const lblStyle = { style: { fontSize: "10px", colors: C.slate } };

// ─── Opções de métrica ────────────────────────────────────────────────────────
const METRICA_OPTS = [
    { key: "cxu", label: "Cx. Unit." },
    { key: "cxf", label: "Cx. Física" },
    { key: "cubos", label: "Cubos" },
];

// ─── Sub-componentes locais ───────────────────────────────────────────────────
function PctBadge({ value }) {
    const high = n(value) > 5;
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full font-bold text-[10px] ${high ? "bg-rose-50 text-rose-600" : "bg-green-50 text-green-600"
                }`}
        >
            {fmtP(value)}
        </span>
    );
}

function SectionHeader({ icon, title, sub, color = "teal" }) {
    const colorMap = {
        teal: "text-teal-600",
        blue: "text-blue-600",
        rose: "text-rose-600",
        amber: "text-amber-600",
        violet: "text-violet-600",
    };
    return (
        <div className="flex items-center gap-3 mb-4 mt-4">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-card shadow-card border border-border">
                <i className={`${icon} ${colorMap[color] ?? colorMap.teal} text-sm`} />
            </div>
            <div>
                <h2 className="text-sm font-bold text-foreground leading-tight">{title}</h2>
                {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
            </div>
        </div>
    );
}

// Análise atual (todo volume/retorno do SAP) ou por Carga de Entrega (só transportes que existem na escala)
const MODOS = [
    { key: "PADRAO", label: "Análise atual", icon: "fa fa-database" },
    { key: "POR_CARGA", label: "Por Carga de Entrega", icon: "fa fa-truck" },
];
const DATA_MINIMA_POR_CARGA = "2026-09-01";

// ─── Download do filtro e comparativo de transportes ──────────────────────────
function SecaoDownload({ descricaoFiltro, baixando, onBaixar }) {
    return (
        <section id="download-dados" className="rounded-2xl border border-border bg-card p-5 shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0">
                    <h3 className="flex items-center gap-2 text-sm font-bold text-foreground">
                        <span className="h-4 w-[3px] rounded-sm bg-emerald-600" />
                        Download dos dados do filtro
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Planilha Excel com todas as linhas de <strong className="text-foreground">volume (entregas)</strong> e de{" "}
                        <strong className="text-foreground">retorno</strong> do filtro selecionado: {descricaoFiltro}.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onBaixar}
                    disabled={baixando}
                    className="inline-flex h-10 items-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white shadow-card transition-all duration-150 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <i className={baixando ? "fa fa-spinner fa-spin" : "fa fa-file-excel-o"} />
                    {baixando ? "Gerando planilha..." : "Baixar Volume e Retorno (.xlsx)"}
                </button>
            </div>
        </section>
    );
}

function CardContagem({ titulo, valor, detalhe, cor }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 shadow-card" style={{ borderLeft: `4px solid ${cor}` }}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{titulo}</p>
            <p className="mt-1 text-2xl font-extrabold tabular-nums text-foreground">{n(valor).toLocaleString("pt-BR")}</p>
            {detalhe && <p className="mt-0.5 text-[11px] text-muted-foreground">{detalhe}</p>}
        </div>
    );
}

function TabelaDivergentes({ linhas, tipo, busca, onAbrir }) {
    const termo = busca.trim().toLowerCase();
    const filtradas = termo
        ? linhas.filter((l) => `${l.transporte ?? ""} ${l.filial ?? ""} ${l.carga ?? ""}`.toLowerCase().includes(termo))
        : linhas;

    if (!linhas.length) {
        return (
            <p className="py-8 text-center text-xs text-muted-foreground">
                <i className="fa fa-check-circle mr-1.5 text-emerald-600" />
                Nenhum transporte divergente neste período.
            </p>
        );
    }

    return (
        <div className="max-h-[420px] overflow-auto">
            <table className="w-full text-[11px] text-foreground">
                <thead className="sticky top-0 bg-card">
                    <tr className="border-b border-border font-semibold text-muted-foreground">
                        <th className="px-2 py-2 text-left">Transporte</th>
                        <th className="px-2 py-2 text-left">Filial</th>
                        <th className="px-2 py-2 text-left">Data</th>
                        {tipo === "sap" ? (
                            <>
                                <th className="px-2 py-2 text-left">Existe em</th>
                                <th className="px-2 py-2 text-right">Cx.U. Volume</th>
                                <th className="px-2 py-2 text-right">Cx.U. Retorno</th>
                            </>
                        ) : (
                            <th className="px-2 py-2 text-left">Carga</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {filtradas.map((l, i) => (
                        <tr
                            key={`${l.transporte}-${l.filial}-${i}`}
                            className={`border-b border-border transition-colors hover:bg-muted ${onAbrir ? "cursor-pointer" : ""}`}
                            onClick={onAbrir ? () => onAbrir(l) : undefined}
                            onKeyDown={onAbrir ? (e) => { if (e.key === "Enter") onAbrir(l); } : undefined}
                            tabIndex={onAbrir ? 0 : undefined}
                            title={onAbrir ? "Clique para ver a linha completa da entrega/retorno" : undefined}
                        >
                            <td className="px-2 py-2 font-mono font-semibold">{l.transporte}</td>
                            <td className="px-2 py-2 text-muted-foreground">{l.filial ?? "—"}</td>
                            <td className="px-2 py-2 tabular-nums">{l.data ? formatLocalDate(l.data) : "—"}</td>
                            {tipo === "sap" ? (
                                <>
                                    <td className="px-2 py-2">
                                        {l.temVolume && <span className="mr-1 rounded-full bg-teal-600/10 px-2 py-0.5 text-[10px] font-bold text-teal-700">Volume</span>}
                                        {l.temRetorno && <span className="rounded-full bg-rose-600/10 px-2 py-0.5 text-[10px] font-bold text-rose-700">Retorno</span>}
                                    </td>
                                    <td className="px-2 py-2 text-right tabular-nums">{l.temVolume ? fmt(l.caixasUnitariasVolume) : "—"}</td>
                                    <td className="px-2 py-2 text-right tabular-nums text-rose-600">{l.temRetorno ? fmt(l.caixasUnitariasRetorno) : "—"}</td>
                                </>
                            ) : (
                                <td className="px-2 py-2 font-mono text-muted-foreground">{l.carga ?? "—"}</td>
                            )}
                        </tr>
                    ))}
                    {!filtradas.length && (
                        <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Nada encontrado para a busca.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

// Campos de uma linha de volume (entrega) e de retorno, na ordem exibida
const fData = (v) => (v ? formatLocalDate(v) : "—");
const fTexto = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
const fNum = (v) => (v === null || v === undefined ? "—" : fmt(v));
const fInt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("pt-BR"));
const fSimNao = (v) => (v === null || v === undefined ? "—" : v ? "Sim" : "Não");

const CAMPOS_VOLUME = [
    ["Filial", "filial", fTexto], ["Placa", "placa", fTexto], ["Transporte", "transporte", fTexto], ["Carga", "carga", fTexto],
    ["Data remessa", "dataEntrega", fData], ["Data criação", "dataCriacao", fData], ["Data retorno", "dataRetorno", fData],
    ["Tipo de carga", "tipoCarga", fTexto], ["Canal", "canal", fTexto], ["Carga vazia", "cargaVazia", fSimNao],
    ["Editado manualmente", "editadoManualmente", fSimNao], ["Pallets", "quantidadePallet", fInt],
    ["Total de clientes", "totalCliente", fInt], ["Total de notas", "totalNotas", fInt],
    ["Cx. físicas", "caixasFisicas", fNum], ["Cx. unitárias", "caixasUnitarias", fNum], ["Cubos", "cubos", fNum],
    ["Drop size Cx.F.", "dropSizeCxf", fNum], ["Drop size cubo", "dropSizeCubo", fNum],
    ["Peso", "peso", fNum], ["Taxa de peso", "taxaPeso", fTexto], ["Capacidade cúbica", "capacidadeCubica", fNum],
    ["Capacidade de peso", "capacidadePeso", fNum], ["Taxa de ocupação", "taxaOcupacao", fTexto],
    ["Km planejado", "kmPlanejado", fInt], ["Km realizado", "kmRealizado", fInt], ["Dispersão", "dispersao", fTexto],
    ["Status fornecimento", "statusFornecimento", fTexto], ["Status PCO", "statusPco", fTexto],
    ["Duração", "duracao", fTexto], ["Hora início", "horaInicio", fTexto], ["Hora final", "horaFinal", fTexto],
];

const CAMPOS_RETORNO = [
    ["Filial", "filial", fTexto], ["Data remessa", "dataEntrega", fData], ["Data retorno", "dataRetorno", fData],
    ["Data criação", "dataCriacao", fData], ["Transporte", "transporte", fTexto], ["Carga", "carga", fTexto],
    ["Canal", "canal", fTexto], ["Motorista", "motoristaCodigo", fTexto], ["Veículo", "veiculoCodigo", fTexto],
    ["Vendedor", "codigoVendedor", fTexto], ["Cód. cliente", "clienteCodigo", fTexto],
    ["Cliente", "clienteRazaoSocial", fTexto], ["Fantasia", "clienteFantasia", fTexto], ["Nota fiscal", "notaFiscal", fTexto],
    ["Cód. produto", "produtoCodigo", fTexto], ["Produto", "produtoNome", fTexto],
    ["Cód. motivo", "motivoCodigo", fTexto], ["Motivo", "motivoNome", fTexto],
    ["Cx. físicas", "caixasFisicas", fNum], ["Cx. unitárias", "caixasUnitarias", fNum], ["Cubos", "cubos", fNum],
    ["Valor total", "valorTotal", fNum], ["Carga vazia", "cargaVazia", fSimNao], ["Editado manualmente", "editadoManualmente", fSimNao],
];

function ModalDetalheTransporte({ linha, detalhe, carregando, onClose }) {
    const volumes = detalhe?.volumes ?? [];
    const retornos = detalhe?.retornos ?? [];

    return (
        <Dialog
            header={`Transporte ${linha?.transporte ?? ""} — linha completa da entrega/retorno`}
            visible={Boolean(linha)}
            onHide={onClose}
            blockScroll
            className="w-[95vw] max-w-[1400px]"
        >
            {carregando ? (
                <Loading />
            ) : (
                <div className="space-y-6">
                    {detalhe?.periodoInicial && (
                        <p className="text-xs text-muted-foreground">
                            Período do filtro: {formatLocalDate(detalhe.periodoInicial)} a {formatLocalDate(detalhe.periodoFinal)}.
                        </p>
                    )}

                    <section className="space-y-3">
                        <h4 className="text-sm font-bold text-foreground">Entrega (volume) — {volumes.length} linha(s)</h4>
                        {!volumes.length && <p className="text-xs text-muted-foreground">Nenhuma linha de volume para este transporte no filtro.</p>}
                        {volumes.map((v) => (
                            <div key={v.id} className="rounded-xl border border-border bg-card p-4">
                                <dl className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4 xl:grid-cols-6">
                                    {CAMPOS_VOLUME.map(([rotulo, chave, formato]) => (
                                        <div key={chave} className="min-w-0">
                                            <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{rotulo}</dt>
                                            <dd className="break-words text-xs font-medium text-foreground">{formato(v[chave])}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        ))}
                    </section>

                    <section className="space-y-3">
                        <h4 className="text-sm font-bold text-foreground">Retorno — {retornos.length} linha(s)</h4>
                        {!retornos.length ? (
                            <p className="text-xs text-muted-foreground">Nenhuma linha de retorno para este transporte no filtro.</p>
                        ) : (
                            <div className="max-h-[420px] overflow-auto rounded-xl border border-border">
                                <table className="w-full whitespace-nowrap text-[11px] text-foreground">
                                    <thead className="sticky top-0 bg-card">
                                        <tr className="border-b border-border font-semibold text-muted-foreground">
                                            {CAMPOS_RETORNO.map(([rotulo]) => (
                                                <th key={rotulo} className="px-2 py-2 text-left">{rotulo}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {retornos.map((r) => (
                                            <tr key={r.id} className="border-b border-border hover:bg-muted">
                                                {CAMPOS_RETORNO.map(([rotulo, chave, formato]) => (
                                                    <td key={rotulo} className="px-2 py-2">{formato(r[chave])}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </div>
            )}
        </Dialog>
    );
}

function SecaoComparativoTransportes({ comparativo, carregando, descricaoFiltro, onAbrirTransporte }) {
    const [busca, setBusca] = useState("");
    const c = comparativo ?? {};
    const sapSemCarga = c.sapSemCargaLista ?? [];
    const cargaSemSap = c.cargaSemSapLista ?? [];

    return (
        <section id="comparativo-transportes" className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="flex items-center gap-2 text-sm font-bold text-foreground">
                        <span className="h-4 w-[3px] rounded-sm bg-violet-600" />
                        Comparativo de transportes — Entrega/Retorno × Carga de Entrega
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Transportes que existem no volume ou no retorno do SAP e não existem na Carga de Entrega, e o contrário.
                        {" "}O número do transporte é comparado sem espaços nem zeros à esquerda ({descricaoFiltro}).
                        {c.periodoInicial && (
                            <> Período comparado: <strong className="text-foreground">{formatLocalDate(c.periodoInicial)}</strong> a{" "}
                                <strong className="text-foreground">{formatLocalDate(c.periodoFinal)}</strong>
                                {" "}(a Carga de Entrega começa em 01/09/2026).</>
                        )}
                    </p>
                </div>
                <input
                    type="search"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar transporte, filial ou carga"
                    className="h-9 w-full rounded-md border border-input px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 sm:w-72"
                />
            </div>

            {carregando ? (
                <Loading />
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
                        <CardContagem titulo="Transportes no SAP" valor={c.transportesSap}
                            detalhe={`${n(c.transportesVolume)} no volume · ${n(c.transportesRetorno)} no retorno`} cor={C.teal} />
                        <CardContagem titulo="Transportes na Carga" valor={c.transportesCarga} detalhe="Carga de Entrega" cor={C.blue} />
                        <CardContagem titulo="Existem nos dois" valor={c.emAmbos} detalhe="SAP e Carga de Entrega" cor={C.green} />
                        <CardContagem titulo="No SAP e sem Carga" valor={c.sapSemCarga}
                            detalhe={`${n(c.volumeSemCarga)} com volume · ${n(c.retornoSemCarga)} com retorno`} cor={C.rose} />
                        <CardContagem titulo="Na Carga e sem SAP" valor={c.cargaSemSap}
                            detalhe={`${n(c.cargaSemVolume)} sem volume (inclui os sem retorno)`} cor={C.amber} />
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        <div className="rounded-2xl border border-border bg-card shadow-card">
                            <div className="border-b border-border px-4 py-3 text-xs font-bold text-foreground">
                                No volume/retorno e sem Carga de Entrega ({n(c.sapSemCarga)})
                            </div>
                            <div className="p-3"><TabelaDivergentes linhas={sapSemCarga} tipo="sap" busca={busca} onAbrir={onAbrirTransporte} /></div>
                        </div>
                        <div className="rounded-2xl border border-border bg-card shadow-card">
                            <div className="border-b border-border px-4 py-3 text-xs font-bold text-foreground">
                                Na Carga de Entrega e sem volume/retorno ({n(c.cargaSemSap)})
                            </div>
                            <div className="p-3"><TabelaDivergentes linhas={cargaSemSap} tipo="carga" busca={busca} /></div>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}

// Data usada para filtrar o período na análise por carga
const CAMPOS_DATA = [
    { key: "REMESSA", label: "Data de remessa" },
    { key: "CRIACAO", label: "Data de criação" },
];

function CampoDataToggle({ value, onChange }) {
    return (
        <div className="inline-flex items-center gap-2" title="Data usada para filtrar o período do volume e do retorno">
            <span className="text-xs font-medium text-muted-foreground">Filtrar por</span>
            <div className="inline-flex rounded-xl bg-muted p-1">
                {CAMPOS_DATA.map((c) => (
                    <button
                        key={c.key}
                        type="button"
                        onClick={() => onChange(c.key)}
                        className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${value === c.key
                            ? "bg-card text-foreground shadow-card"
                            : "text-muted-foreground hover:text-foreground"
                            }`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function ModoToggle({ value, onChange }) {
    return (
        <div className="inline-flex rounded-xl bg-muted p-1">
            {MODOS.map((m) => (
                <button
                    key={m.key}
                    type="button"
                    onClick={() => onChange(m.key)}
                    className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${value === m.key
                        ? "bg-card text-foreground shadow-card"
                        : "text-muted-foreground hover:text-foreground"
                        }`}
                >
                    <i className={m.icon} />
                    {m.label}
                </button>
            ))}
        </div>
    );
}

function MetricaToggle({ value, onChange }) {
    return (
        <div className="flex gap-1">
            {METRICA_OPTS.map((opt) => (
                <button
                    key={opt.key}
                    onClick={() => onChange(opt.key)}
                    className={`px-3 py-1 rounded-full text-[10px] font-semibold border transition-all ${value === opt.key
                        ? "bg-teal-600 text-white border-teal-600"
                        : "bg-card text-muted-foreground border-border hover:border-teal-300"
                        }`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

function KpiPorFilial({ label, filial, metrica }) {
    const valorEntregue = n(filial[metrica.ent]);
    const valorRetornado = n(filial[metrica.ret]);
    const efetivo = valorEntregue - valorRetornado;
    const meta = n(filial[metrica.meta]);
    const pctMeta = n(filial[metrica.pctMeta]);
    const ly = n(filial[metrica.ly]);
    const pctLy = n(filial[metrica.pctLy]);

    return (
        <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
            <div className="flex items-center justify-between gap-3 bg-muted p-4">
                <span className="text-sm font-bold text-foreground">{label ? label : filial.filial}</span>
                <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-bold text-teal-700">
                    {metrica.label}
                </span>
            </div>
            <div className="p-4 space-y-2.5">
                <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Total entregue</span>
                    <span className="text-lg font-black tabular-nums text-foreground">{fmt(valorEntregue, 2)}</span>
                </div>
                <div className="flex items-center justify-end gap-1.5">
                    <span className="text-[11px] font-bold text-rose-600 tabular-nums">(-) {fmtAbrev(valorRetornado)}</span>
                    <span className="text-[9px] font-bold uppercase tracking-widest text-rose-500">Retornos</span>
                </div>

                <div className="space-y-1.5 border-t border-border pt-2.5">
                    <div className="flex items-center justify-between text-[11px]">
                        <span className="font-semibold text-muted-foreground">Entreg. Efetiv.</span>
                        <span className="font-bold tabular-nums text-foreground">{fmt(efetivo, 2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                        <span className="font-semibold text-muted-foreground">Prev. Entreg.</span>
                        <span className="font-bold tabular-nums text-foreground">{fmt(meta, 0)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                        <span className="font-semibold text-muted-foreground">Meta</span>
                        <span className="flex items-center gap-2">
                            <span className="font-bold tabular-nums text-foreground">{fmtAbrev(meta)}</span>
                            <span className={`font-black ${pctMeta >= 100 ? "text-green-600" : "text-rose-600"}`}>
                                {fmtP(pctMeta)}
                            </span>
                        </span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                        <span className="font-semibold text-muted-foreground">LY</span>
                        <span className="flex items-center gap-2">
                            <span className="font-bold tabular-nums text-foreground">{fmtAbrev(ly)}</span>
                            <span className={`font-black ${pctLy >= 0 ? "text-green-600" : "text-rose-600"}`}>
                                {fmtP(pctLy)}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Tabela: Detalhamento por Filial ─────────────────────────────────────────
function TabelaFilial({ porFilial, consolidado }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-[11px] text-foreground">
                <thead>
                    <tr className="border-b border-border text-muted-foreground font-semibold">
                        <th className="text-left py-2 px-2">Filial</th>
                        <th className="text-right py-2 px-2 whitespace-nowrap">Cx.U. Entregue</th>
                        <th className="text-right py-2 px-2 whitespace-nowrap">Cx.U. Retorno</th>
                        <th className="text-right py-2 px-2">% Cx.U.</th>
                        <th className="text-right py-2 px-2 whitespace-nowrap">Cx.F. Entregue</th>
                        <th className="text-right py-2 px-2 whitespace-nowrap">Cx.F. Retorno</th>
                        <th className="text-right py-2 px-2">% Cx.F.</th>
                        <th className="text-right py-2 px-2 whitespace-nowrap">Cubos Entregue</th>
                        <th className="text-right py-2 px-2 whitespace-nowrap">Cubos Retorno</th>
                        <th className="text-right py-2 px-2">% Cubos</th>
                    </tr>
                </thead>
                <tbody>
                    {porFilial.map((f, i) => (
                        <tr key={i} className="border-b border-border hover:bg-muted transition-colors">
                            <td className="py-2 px-2 font-semibold text-foreground">{f.filial}</td>
                            <td className="py-2 px-2 text-right tabular-nums text-teal-700 font-semibold">{fmt(f.caixasUnitariasEntregues)}</td>
                            <td className="py-2 px-2 text-right tabular-nums text-rose-600">{fmt(f.caixasUnitariasRetornadas)}</td>
                            <td className="py-2 px-2 text-right"><PctBadge value={f.percentualCaixasUnitarias} /></td>
                            <td className="py-2 px-2 text-right tabular-nums text-teal-700 font-semibold">{fmt(f.caixasFisicasEntregues)}</td>
                            <td className="py-2 px-2 text-right tabular-nums text-rose-600">{fmt(f.caixasFisicasRetornadas)}</td>
                            <td className="py-2 px-2 text-right"><PctBadge value={f.percentualCaixasFisicas} /></td>
                            <td className="py-2 px-2 text-right tabular-nums text-teal-700 font-semibold">{fmt(f.cubosEntregues)}</td>
                            <td className="py-2 px-2 text-right tabular-nums text-rose-600">{fmt(f.cubosRetornados)}</td>
                            <td className="py-2 px-2 text-right"><PctBadge value={f.percentualCubos} /></td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="border-t-2 border-border bg-muted font-bold text-foreground">
                        <td className="py-2 px-2">TOTAL</td>
                        <td className="py-2 px-2 text-right tabular-nums text-teal-700">{fmt(consolidado.caixasUnitariasEntregues)}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-rose-600">{fmt(consolidado.caixasUnitariasRetornadas)}</td>
                        <td className="py-2 px-2 text-right"><PctBadge value={consolidado.percentualCaixasUnitarias} /></td>
                        <td className="py-2 px-2 text-right tabular-nums text-teal-700">{fmt(consolidado.caixasFisicasEntregues)}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-rose-600">{fmt(consolidado.caixasFisicasRetornadas)}</td>
                        <td className="py-2 px-2 text-right"><PctBadge value={consolidado.percentualCaixasFisicas} /></td>
                        <td className="py-2 px-2 text-right tabular-nums text-teal-700">{fmt(consolidado.cubosEntregues)}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-rose-600">{fmt(consolidado.cubosRetornados)}</td>
                        <td className="py-2 px-2 text-right"><PctBadge value={consolidado.percentualCubos} /></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

// ─── Tabela: Detalhamento de Motivos ─────────────────────────────────────────
function TabelaMotivos({ motivos }) {
    return (
        <div className="overflow-x-auto max-h-[335px]">
            <table className="w-full text-[11px] text-foreground">
                <thead>
                    <tr className="border-b border-border text-muted-foreground font-semibold">
                        <th className="text-left py-2 px-2">#</th>
                        <th className="text-left py-2 px-2">Código</th>
                        <th className="text-left py-2 px-2">Motivo</th>
                        <th className="text-right py-2 px-2">Cx. Unitárias</th>
                        <th className="text-right py-2 px-2">Cx. Físicas</th>
                        <th className="text-right py-2 px-2">Cubos</th>
                    </tr>
                </thead>
                <tbody>
                    {[...motivos]
                        .sort((a, b) => n(b.caixasUnitarias) - n(a.caixasUnitarias))
                        .map((m, i) => (
                            <tr key={i} className="border-b border-border hover:bg-muted transition-colors">
                                <td className="py-2 px-2 text-muted-foreground">{i + 1}</td>
                                <td className="py-2 px-2 font-mono text-muted-foreground">{m.codigo ?? "—"}</td>
                                <td className="py-2 px-2 font-semibold text-foreground">{m.motivo ?? "—"}</td>
                                <td className="py-2 px-2 text-right tabular-nums text-rose-600 font-semibold">{fmt(m.caixasUnitarias)}</td>
                                <td className="py-2 px-2 text-right tabular-nums text-violet-600">{fmt(m.caixasFisicas)}</td>
                                <td className="py-2 px-2 text-right tabular-nums text-amber-600">{fmt(m.cubos)}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
}

// ─── Scorecard consolidado ────────────────────────────────────────────────────
function ScorecardConsolidado({ consolidado }) {
    const items = [
        {
            label: "Cx. Unitárias",
            ent: consolidado.caixasUnitariasEntregues,
            ret: consolidado.caixasUnitariasRetornadas,
            pct: consolidado.percentualCaixasUnitarias,
        },
        {
            label: "Cx. Físicas",
            ent: consolidado.caixasFisicasEntregues,
            ret: consolidado.caixasFisicasRetornadas,
            pct: consolidado.percentualCaixasFisicas,
        },
        {
            label: "Cubos",
            ent: consolidado.cubosEntregues,
            ret: consolidado.cubosRetornados,
            pct: consolidado.percentualCubos,
        },
    ];

    return (
        <div className="grid grid-cols-3 gap-4 mt-2">
            {items.map((item) => {
                const pctVal = n(item.pct);
                const barPct = (Math.min(pctVal, 20) / 20) * 100;
                const isHigh = pctVal > 5;
                return (
                    <div key={item.label} className="border border-border rounded-xl p-4 space-y-3">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                            {item.label}
                        </p>
                        <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Entregue</span>
                            <span className="font-bold text-teal-700 tabular-nums">{fmt(item.ent, 0)}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Retornado</span>
                            <span className="font-bold text-rose-600 tabular-nums">{fmt(item.ret, 0)}</span>
                        </div>
                        <div>
                            <div className="flex justify-between text-[10px] mb-1">
                                <span className="text-muted-foreground">% Retorno</span>
                                <span className={`font-black ${isHigh ? "text-rose-600" : "text-green-600"}`}>
                                    {fmtP(item.pct)}
                                </span>
                            </div>
                            <div className="bg-muted rounded-full h-2 overflow-hidden">
                                <div
                                    className={`h-2 rounded-full transition-all duration-700 ${isHigh ? "bg-rose-500" : "bg-green-500"
                                        }`}
                                    style={{ width: `${barPct}%` }}
                                />
                            </div>
                            <p className="text-[9px] text-muted-foreground/50 mt-0.5 text-right">escala 0–20%</p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════════
export default function DashboardEntregaRetorno() {
    const { user } = useAuth();
    const isRoleComissao = user.perfis.includes("COMISSOES");

    const filialService = useMemo(() => new FilialService(), []);
    const relatorioService = new RelatorioEntregasService();

    const [loading, setLoading] = useState(true);
    const [filiais, setFiliais] = useState([]);
    const [filiaisSelecionadas, setFiliaisSelecionadas] = useState([]);
    const [dataInicial, setDataInicial] = useState(getMesAtual());
    const [dataFinal, setDataFinal] = useState(getHojeFormatado());
    const [relatorio, setRelatorio] = useState({});
    const [metrica, setMetrica] = useState("cxu");
    const [modo, setModo] = useState("PADRAO");
    const porCarga = modo === "POR_CARGA";
    const [campoData, setCampoData] = useState("REMESSA");
    const [baixandoImagem, setBaixandoImagem] = useState(false);
    const [baixandoPlanilha, setBaixandoPlanilha] = useState(false);
    const [comparativoTransportes, setComparativoTransportes] = useState(null);
    const [carregandoComparativo, setCarregandoComparativo] = useState(true);
    const [transporteAberto, setTransporteAberto] = useState(null);
    const [detalheTransporte, setDetalheTransporte] = useState(null);
    const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
    const [dataGeracaoImagem, setDataGeracaoImagem] = useState(null);
    const [exibicao, setExibicao] = useState("table")

    const [filtroAplicado, setFiltroAplicado] = useState({ dataInicial: getMesAtual(), dataFinal: getHojeFormatado() });

    const periodoValido = Boolean(dataInicial) && Boolean(dataFinal) && dataInicial <= dataFinal;
    const mesesNoIntervalo = useMemo(() => {
        const [anoIni, mesIni] = filtroAplicado.dataInicial.split("-").map(Number);
        const [anoFim, mesFim] = filtroAplicado.dataFinal.split("-").map(Number);
        return (anoFim - anoIni) * 12 + (mesFim - mesIni) + 1;
    }, [filtroAplicado]);
    const periodoSelecionado = periodoValido
        ? `${formatLocalDate(dataInicial)} a ${formatLocalDate(dataFinal)}`
        : "Período inválido";
    const filiaisExibidas = isRoleComissao ? [user.filialId] : filiaisSelecionadas;
    const filiaisSelecionadasLabel = filiaisExibidas.length
        ? filiaisExibidas
            .map((filialId) => filiais.find((filial) => filial.id === filialId)?.nome ?? `Filial ${filialId}`)
            .join(", ")
        : "Todas as filiais";
    const descricaoFiltroDados = [
        periodoSelecionado,
        filiaisSelecionadasLabel,
        porCarga ? `por Carga de Entrega, pela ${campoData === "CRIACAO" ? "data de criação" : "data de remessa"}` : "análise atual",
    ].join(" · ");
    const formatarDataHora = (data) => new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
    }).format(data);
    const criarNomeArquivoImagem = (data) => {
        const dataArquivo = [
            data.getFullYear(),
            String(data.getMonth() + 1).padStart(2, "0"),
            String(data.getDate()).padStart(2, "0"),
        ].join("-");

        return `relatorio-entregas-retornos${porCarga ? `-por-carga${campoData === "CRIACAO" ? "-criacao" : ""}` : ""}-${dataInicial}-a-${dataFinal}-${dataArquivo}`
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-zA-Z0-9]+/g, "-")
            .replace(/^-|-$/g, "")
            .toLowerCase() + ".png";
    };

    // Views por seção (chart | table)
    const [viewEvolucao, setViewEvolucao] = useState("chart");
    const [viewComparativo, setViewComparativo] = useState("chart");

    useEffect(() => {
        buscarFiliais();
    }, []);

    // Aguarda o usuário parar de alterar as datas antes de aplicar o filtro,
    // evitando recarregar o relatório a cada clique no calendário.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!periodoValido) {
                toast.error("A data inicial não pode ser posterior à data final.");
                return;
            }

            setFiltroAplicado({ dataInicial, dataFinal });
        }, 600);

        return () => clearTimeout(timer);
    }, [dataInicial, dataFinal, periodoValido]);

    useEffect(() => {
        setLoading(true);
        relatorioService
            .relatorioEntregas({
                filiais: isRoleComissao ? [user.filialId] : filiaisSelecionadas,
                dataInicial: filtroAplicado.dataInicial,
                dataFinal: filtroAplicado.dataFinal,
                modo,
                campoData: porCarga ? campoData : undefined,
            })
            .then((res) => setRelatorio(res.data))
            .finally(() => setLoading(false));
    }, [filtroAplicado, filiaisSelecionadas, isRoleComissao, user.filialId, modo, campoData, porCarga]);

    // filtro selecionado (o mesmo do relatório) para o download e o comparativo
    const parametrosFiltro = () => ({
        filiais: isRoleComissao ? [user.filialId] : filiaisSelecionadas,
        dataInicial: filtroAplicado.dataInicial,
        dataFinal: filtroAplicado.dataFinal,
        modo,
        campoData: porCarga ? campoData : undefined,
    });

    useEffect(() => {
        let cancelado = false;
        setCarregandoComparativo(true);
        relatorioService
            .comparativoTransportes(parametrosFiltro())
            .then((res) => { if (!cancelado) setComparativoTransportes(res.data); })
            .catch(() => { if (!cancelado) setComparativoTransportes(null); })
            .finally(() => { if (!cancelado) setCarregandoComparativo(false); });

        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroAplicado, filiaisSelecionadas, isRoleComissao, user.filialId, modo, campoData, porCarga]);

    // abre a linha clicada do comparativo com todas as linhas de volume e retorno do transporte
    const abrirTransporte = (linha) => {
        setTransporteAberto(linha);
        setDetalheTransporte(null);
        setCarregandoDetalhe(true);
        relatorioService
            .detalheTransporte(parametrosFiltro(), linha.transporte)
            .then((res) => setDetalheTransporte(res.data))
            .catch(() => setTransporteAberto(null))
            .finally(() => setCarregandoDetalhe(false));
    };

    const baixarPlanilha = async () => {
        setBaixandoPlanilha(true);
        const toastId = toast.loading("Gerando a planilha de volume e retorno...");
        try {
            const res = await relatorioService.exportarVolumeRetorno(parametrosFiltro());
            const contentDisposition = res.headers["content-disposition"];
            let nomeArquivo = "volume-e-retorno.xlsx";
            const match = contentDisposition?.match(/filename="?([^"]+)"?/);
            if (match?.length > 1) nomeArquivo = match[1];

            FileDownload(res.data, nomeArquivo);
            toast.update(toastId, { render: "Planilha baixada com sucesso!", type: "success", isLoading: false, autoClose: 3000 });
        } catch (e) {
            toast.update(toastId, {
                render: e?.message || "Não foi possível gerar a planilha.",
                type: "error", isLoading: false, autoClose: 5000,
            });
        } finally {
            setBaixandoPlanilha(false);
        }
    };

    // na análise por carga nada anterior a 01/09/2026 entra: a tela já mostra o período realmente consultado
    const trocarModo = (novo) => {
        setModo(novo);
        if (novo === "POR_CARGA" && dataInicial && dataInicial < DATA_MINIMA_POR_CARGA) {
            setDataInicial(DATA_MINIMA_POR_CARGA);
        }
    };

    const { consolidado = {}, evolucao = [], motivos = [], porFilial = [] } = relatorio;
    const dadosComparativoAno = relatorio.comparativoMensal
        ?? relatorio.comparativoAnual
        ?? relatorio.comparativoAnoAtualAnterior
        ?? relatorio.comparativo
        ?? [];
    const comparativoAno = Array.isArray(dadosComparativoAno) ? dadosComparativoAno : [];

    const buscarFiliais = () => {
        filialService
            .listar({ tipoDistribuidor: "LOGISTICO" })
            .then((res) => setFiliais(res.data));
    };

    const baixarImagem = async () => {
        if (baixandoImagem) return;

        const dataGeracao = new Date();
        setBaixandoImagem(true);
        setDataGeracaoImagem(dataGeracao);
        const toastId = toast.loading("Baixando imagem do relatório...");
        const imagemGerada = await captureElementToImage(
            "relatorio-entregas",
            criarNomeArquivoImagem(dataGeracao),
            "motivo-de-retorno",
            { scale: 1.5 }
        );

        toast.update(toastId, imagemGerada
            ? { render: "Imagem baixada com sucesso!", type: "success", isLoading: false, autoClose: 3000 }
            : { render: "Não foi possível gerar a imagem.", type: "error", isLoading: false, autoClose: 4000 });
        setBaixandoImagem(false);
        setDataGeracaoImagem(null);
    };

    const MK = {
        cxu: {
            ent: "caixasUnitariasEntregues",
            ret: "caixasUnitariasRetornadas",
            pct: "percentualCaixasUnitarias",
            label: "Cx. Unitárias",
            motKey: "caixasUnitarias",
            periodoAtual: "caixasUnitariasPeriodoAtual",
            periodoAnterior: "caixasUnitariasPeriodoAnterior",
            meta: "metaCaixasUnitarias",
            pctMeta: "percentualMetaCaixasUnitarias",
            ly: "caixasUnitariasEfetivasAnoAnterior",
            pctLy: "percentualCrescimentoCaixasUnitarias",
        },
        cxf: {
            ent: "caixasFisicasEntregues",
            ret: "caixasFisicasRetornadas",
            pct: "percentualCaixasFisicas",
            label: "Cx. Físicas",
            motKey: "caixasFisicas",
            periodoAtual: "caixasFisicasPeriodoAtual",
            periodoAnterior: "caixasFisicasPeriodoAnterior",
            meta: "metaCaixasFisicas",
            pctMeta: "percentualMetaCaixasFisicas",
            ly: "caixasFisicasEfetivasAnoAnterior",
            pctLy: "percentualCrescimentoCaixasFisicas",
        },
        cubos: {
            ent: "cubosEntregues",
            ret: "cubosRetornados",
            pct: "percentualCubos",
            label: "Cubos",
            motKey: "cubos",
            periodoAtual: "cubosPeriodoAtual",
            periodoAnterior: "cubosPeriodoAnterior",
            meta: "metaCubos",
            pctMeta: "percentualMetaCubos",
            ly: "cubosEfetivosAnoAnterior",
            pctLy: "percentualCrescimentoCubos",
        },
    };
    const mk = MK[metrica];

    const evolucaoParaGrafico = useMemo(() => {
        if (mesesNoIntervalo <= 1) {
            return evolucao.map((item) => ({
                ...item,
                mes: Number(String(item.data ?? "").slice(5, 7)) || null,
            }));
        }

        return Object.values(evolucao.reduce((porMes, item) => {
            const { chave, label } = mesDaData(item.data);
            const acumulado = porMes[chave] ?? {
                data: label,
                mes: Number(chave.slice(-2)) || null,
                ...Object.fromEntries(Object.values(MK).flatMap((m) => [[m.ent, 0], [m.ret, 0]])),
            };

            Object.values(MK).forEach((m) => {
                acumulado[m.ent] += n(item[m.ent]);
                acumulado[m.ret] += n(item[m.ret]);
            });

            porMes[chave] = acumulado;
            return porMes;
        }, {}));
    }, [evolucao, mesesNoIntervalo]);

    const metaPorMes = useMemo(() => {
        const mapa = {};
        comparativoAno.forEach((item) => {
            mapa[n(item.mes)] = item[mk.meta];
        });
        return mapa;
    }, [comparativoAno, metrica]);

    // ── Configurações dos gráficos ────────────────────────────────────────────

    const chartEvolucao = useMemo(() => {
        const cats = evolucaoParaGrafico.map((e) => String(e.data ?? ""));
        return {
            options: {
                chart: { ...baseChart, type: "area", animations: { enabled: false } },
                colors: [C.tealMid, C.rose, C.green],
                stroke: { curve: "smooth", width: [2, 2, 2], dashArray: [0, 0, 4] },
                fill: { type: "gradient", gradient: { opacityFrom: 0.50, opacityTo: 0.02 } },
                dataLabels: {
                    enabled: true,
                    formatter: (valor) => (valor == null ? "" : fmt(valor, 0)),
                    background: { enabled: true, borderRadius: 3, opacity: 0.85 },
                    style: { fontSize: "10px", fontWeight: 600 },
                },
                xaxis: { categories: cats, labels: { rotate: -30, ...lblStyle, tickAmount: 10 } },
                yaxis: { labels: { formatter: (v) => fmt(v, 0), ...lblStyle } },
                legend: { position: "top", fontSize: "11px" },
                markers: { size: 4, hover: { size: 7 } },
                tooltip: { ...baseTip, y: { formatter: (v) => fmt(v) } },
                grid: baseGrid,
            },
            series: [
                { name: `${mk.label} Entregues`, data: evolucaoParaGrafico.map((e) => +n(e[mk.ent]).toFixed(2)) },
                { name: `${mk.label} Retornadas`, data: evolucaoParaGrafico.map((e) => +n(e[mk.ret]).toFixed(2)) }
            ],
        };
    }, [evolucaoParaGrafico, metrica, metaPorMes]);

    const chartEvolucaoPct = useMemo(() => {
        const agrupadoPorMes = mesesNoIntervalo > 1;
        const cats = evolucaoParaGrafico.map((e) => String(e.data ?? ""));
        return {
            options: {
                chart: { ...baseChart, type: "line", },
                colors: [C.amber],
                stroke: { curve: "smooth", width: 2, dashArray: 4 },
                dataLabels: { enabled: false },
                xaxis: { categories: cats, labels: { rotate: -30, ...lblStyle, tickAmount: 10 } },
                yaxis: { labels: { formatter: (v) => `${fmt(v, 1)}%`, ...lblStyle } },
                legend: { position: "top", fontSize: "11px" },
                tooltip: { ...baseTip, y: { formatter: (v) => `${fmt(v)}%` } },
                grid: baseGrid,
                markers: { size: 4, hover: { size: 7 } },
                annotations: {
                    yaxis: [
                        {
                            y: 1,
                            borderColor: C.rose,
                            borderWidth: 1,
                            strokeDashArray: 4,
                            label: {
                                text: "Meta 1%",
                                style: { fontSize: "10px", color: C.rose, background: "#fff" },
                            },
                        },
                    ],
                },
            },
            series: [
                {
                    name: `% Retorno ${mk.label}`,
                    data: evolucaoParaGrafico.map((e) => {
                        const percentual = agrupadoPorMes
                            ? (n(e[mk.ent]) > 0 ? (n(e[mk.ret]) / n(e[mk.ent])) * 100 : 0)
                            : n(e[mk.pct]);
                        return +percentual.toFixed(2);
                    }),
                },
            ],
        };
    }, [evolucaoParaGrafico, mesesNoIntervalo, metrica]);

    const chartComparativoAno = useMemo(() => {
        const dados = [...comparativoAno].sort((a, b) => (n(a.ano) - n(b.ano)) || (n(a.mes) - n(b.mes)));
        const valorOuNulo = (valor) => (valor == null ? null : +n(valor).toFixed(2));

        return {
            options: {
                chart: { ...baseChart, type: "line" },
                colors: [C.tealMid, C.amber, C.green],
                stroke: { curve: "smooth", width: [2.5, 2.5, 2], dashArray: [0, 0, 3] },
                dataLabels: {
                    enabled: true,
                    formatter: (valor) => (valor == null ? "" : fmt(valor, 0)),
                    style: { fontSize: "10px", fontWeight: 600, colors: [C.tealMid, C.amber, C.green] },
                },
                xaxis: {
                    categories: dados.map((item) => mesAnoLabel(item)),
                    labels: { ...lblStyle },
                },
                yaxis: { labels: { formatter: (valor) => fmt(valor, 0), ...lblStyle } },
                legend: { position: "top", fontSize: "11px" },
                markers: { size: 5, hover: { size: 7 } },
                tooltip: { ...baseTip, y: { formatter: (valor) => fmt(valor) } },
                grid: baseGrid,
            },
            series: [
                { name: "Período Atual", data: dados.map((item) => valorOuNulo(item[mk.periodoAtual])) },
                { name: "Período Anterior", data: dados.map((item) => valorOuNulo(item[mk.periodoAnterior])) },
                { name: "Meta", data: dados.map((item) => valorOuNulo(item[mk.meta])) },
            ],
        };
    }, [comparativoAno, metrica]);

    const chartFilialBar = useMemo(() => {
        const cats = porFilial.map((f) => f.filial ?? "");
        return {
            options: {
                chart: { ...baseChart, type: "bar", },
                plotOptions: { bar: { borderRadius: 4, columnWidth: "65%" } },
                colors: [C.tealMid, C.rose],
                dataLabels: { enabled: true, formatter: (v) => `${v?.toFixed(0)}`, style: { fontSize: "10px", colors: ["#fff"] }, },
                xaxis: { categories: cats, labels: { rotate: -30, ...lblStyle } },
                yaxis: { labels: { formatter: (v) => fmt(v, 0), ...lblStyle } },
                legend: { position: "top", fontSize: "11px" },
                tooltip: { ...baseTip, y: { formatter: (v) => fmt(v) } },
                grid: baseGrid,
            },
            series: [
                { name: `${mk.label} Entregues`, data: porFilial.map((f) => +n(f[mk.ent]).toFixed(2)) },
                { name: `${mk.label} Retornadas`, data: porFilial.map((f) => +n(f[mk.ret]).toFixed(2)) },
            ],
        };
    }, [porFilial, metrica]);

    const chartFilialPct = useMemo(() => {
        const sorted = [...porFilial].sort((a, b) => n(b[mk.pct]) - n(a[mk.pct]));
        return {
            options: {
                chart: { ...baseChart, type: "bar", animations: { enabled: false } },
                plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "60%" } },
                colors: [C.rose],
                dataLabels: {
                    enabled: true,
                    formatter: (v) => `${fmt(v, 1)}%`,
                    style: { fontSize: "10px", colors: ["#fff"] },
                },
                xaxis: {
                    categories: sorted.map((f) => f.filial ?? ""),
                    labels: { formatter: (v) => `${v}%`, ...lblStyle },
                },
                yaxis: { labels: { ...lblStyle } },
                tooltip: { ...baseTip, y: { formatter: (v) => `${fmt(v)}%` } },
                grid: baseGrid,
            },
            series: [{ name: "% Retorno", data: sorted.map((f) => +n(f[mk.pct]).toFixed(2)) }],
        };
    }, [porFilial, metrica]);

    const chartMotivosDonut = useMemo(() => {
        const key = mk.motKey;
        const sorted = [...motivos].sort((a, b) => n(b[key]) - n(a[key])).slice(0, 10);
        const vals = sorted.map((m) => +n(m[key]).toFixed(2));
        const total = vals.reduce((a, b) => a + b, 0);
        return {
            options: {
                chart: { ...baseChart, type: "donut", },
                labels: sorted.map((m) => m.motivo ?? m.codigo ?? "—"),
                colors: PALETTE_DONUT,
                legend: { position: "bottom", fontSize: "10px" },
                dataLabels: {
                    enabled: true,
                    formatter: (v) => `${v.toFixed(1)}%`,
                    style: { fontSize: "10px" },
                },
                tooltip: { ...baseTip, y: { formatter: (v) => `${fmt(v)} ${mk.label}` } },
                plotOptions: {
                    pie: {
                        donut: {
                            size: "65%",
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: "Total",
                                    fontSize: "11px",
                                    formatter: () => fmt(total, 0),
                                },
                            },
                        },
                    },
                },
            },
            series: vals,
        };
    }, [motivos, metrica]);

    const chartMotivosBar = useMemo(() => {
        const key = mk.motKey;
        const sorted = [...motivos].sort((a, b) => n(b[key]) - n(a[key])).slice(0, 12);
        const truncate = (s) => (s && s.length > 30 ? s.slice(0, 30) + "…" : (s ?? "—"));
        return {
            options: {
                chart: { ...baseChart, type: "bar", animations: { enabled: false } },
                plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "60%" } },
                colors: [C.rose],
                dataLabels: { enabled: true, formatter: (v) => `${v?.toFixed(0)}`, style: { fontSize: "10px", colors: ["#fff"] }, },
                xaxis: {
                    categories: sorted.map((m) => truncate(m.motivo ?? m.codigo)),
                    labels: { ...lblStyle },
                },
                yaxis: { labels: { style: { fontSize: "10px" }, maxWidth: 200 } },
                tooltip: { ...baseTip, y: { formatter: (v) => `${fmt(v)} ${mk.label}` } },
                grid: baseGrid,
            },
            series: [{ name: mk.label, data: sorted.map((m) => +n(m[key]).toFixed(2)) }],
        };
    }, [motivos, metrica]);

    const chartMotivosComparativo = useMemo(() => {
        const sorted = [...motivos]
            .sort((a, b) => n(b.caixasUnitarias) - n(a.caixasUnitarias))
            .slice(0, 8);
        const truncate = (s) => (s && s.length > 22 ? s.slice(0, 22) + "…" : (s ?? "—"));
        return {
            options: {
                chart: { ...baseChart, type: "bar", animations: { enabled: false } },
                plotOptions: { bar: { borderRadius: 3, horizontal: true, barHeight: "70%" } },
                colors: [C.rose, C.violet, C.amber],
                dataLabels: { enabled: false },
                xaxis: {
                    categories: sorted.map((m) => truncate(m.motivo ?? m.codigo)),
                    labels: { ...lblStyle },
                },
                yaxis: { labels: { style: { fontSize: "10px" }, maxWidth: 170 } },
                legend: { position: "top", fontSize: "11px" },
                tooltip: { ...baseTip, y: { formatter: (v) => fmt(v) } },
                grid: baseGrid,
            },
            series: [
                { name: "Cx. Unitárias", data: sorted.map((m) => +n(m.caixasUnitarias).toFixed(2)) },
                { name: "Cx. Físicas", data: sorted.map((m) => +n(m.caixasFisicas).toFixed(2)) },
                { name: "Cubos", data: sorted.map((m) => +n(m.cubos).toFixed(2)) },
            ],
        };
    }, [motivos]);

    const chartRadial = useMemo(
        () => ({
            options: {
                chart: { ...baseChart, type: "radialBar", animations: { enabled: false } },
                colors: [C.rose, C.amber, C.violet],
                plotOptions: {
                    radialBar: {
                        startAngle: -135,
                        endAngle: 135,
                        hollow: { size: "30%" },
                        dataLabels: {
                            name: { fontSize: "11px" },
                            value: { fontSize: "13px", fontWeight: 700, formatter: (v) => `${v}%` },
                            total: {
                                show: true,
                                label: "Média",
                                fontSize: "11px",
                                formatter: () => {
                                    const avg =
                                        (n(consolidado.percentualCaixasUnitarias) +
                                            n(consolidado.percentualCaixasFisicas) +
                                            n(consolidado.percentualCubos)) /
                                        3;
                                    return `${fmt(avg, 1)}%`;
                                },
                            },
                        },
                    },
                },
                labels: ["% Cx. Unit.", "% Cx. Fís.", "% Cubos"],
                legend: { show: true, position: "bottom", fontSize: "10px" },
                tooltip: { ...baseTip, y: { formatter: (v) => `${v}%` } },
            },
            series: [
                +n(consolidado.percentualCaixasUnitarias).toFixed(2),
                +n(consolidado.percentualCaixasFisicas).toFixed(2),
                +n(consolidado.percentualCubos).toFixed(2),
            ],
        }),
        [consolidado]
    );

    const isEmpty = !evolucao.length && !motivos.length && !porFilial.length && !comparativoAno.length;

    return (
        <div className="bg-background min-h-screen">

            {/* ── Header ── */}
            <div className="flex flex-wrap items-center justify-end gap-3 p-4">
                <div className="flex flex-wrap items-center gap-3">
                    <ModoToggle value={modo} onChange={trocarModo} />
                    {porCarga && <CampoDataToggle value={campoData} onChange={setCampoData} />}
                    <button
                        type="button"
                        onClick={baixarImagem}
                        disabled={baixandoImagem}
                        className="inline-flex h-10 items-center gap-2 rounded-xl bg-foreground px-4 text-sm font-semibold text-background shadow-card transition-all duration-150 hover:brightness-110 hover:shadow-soft focus:outline-none focus:ring-2 focus:ring-foreground/40 focus:ring-offset-2"
                        title="Baixar dashboard como imagem"
                    >
                        <i className="fa fa-image" />
                        {baixandoImagem ? "Gerando imagem..." : "Baixar imagem"}
                    </button>
                    {!isRoleComissao && (
                        <div className="w-64">
                            <CustomMultiSelect
                                value={filiaisSelecionadas}
                                onChange={setFiliaisSelecionadas}
                                options={filiais.map((filial) => ({ label: filial.nome, value: filial.id }))}
                                placeholder="Todas as filiais"
                                filterPlaceholder="Buscar filial..."
                            />
                        </div>
                    )}
                    <div className="flex items-end gap-2">
                        <div className="flex flex-col gap-0.5">
                            <input
                                id="dataInicial"
                                type="date"
                                value={dataInicial}
                                min={porCarga ? DATA_MINIMA_POR_CARGA : undefined}
                                max={dataFinal || undefined}
                                onChange={(e) => setDataInicial(e.target.value)}
                                className={`h-10 rounded-xl border bg-card px-3 text-sm text-foreground shadow-card focus:outline-none focus:ring-2 focus:ring-teal-500/40 ${periodoValido ? "border-border" : "border-rose-400"
                                    }`}
                            />
                        </div>
                        <span className="text-xs text-muted-foreground pb-2.5">até</span>
                        <div className="flex flex-col gap-0.5">
                            <input
                                id="dataFinal"
                                type="date"
                                value={dataFinal}
                                min={dataInicial || undefined}
                                onChange={(e) => setDataFinal(e.target.value)}
                                className={`h-10 rounded-xl border bg-card px-3 text-sm text-foreground shadow-card focus:outline-none focus:ring-2 focus:ring-teal-500/40 ${periodoValido ? "border-border" : "border-rose-400"
                                    }`}
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-3 border-l border-border pl-3">
                        <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-widest whitespace-nowrap">
                            Visualizar por:
                        </span>
                        <MetricaToggle value={metrica} onChange={setMetrica} />
                    </div>
                </div>
            </div>

            <div id="relatorio-entregas" className="p-4 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="text-lg font-black text-foreground tracking-tight">
                            Entregas &amp; Retornos — Operação logística
                            {porCarga && <span className="ml-2 rounded-full bg-teal-600/10 px-2 py-0.5 align-middle text-[10px] font-bold text-teal-700">Por Carga de Entrega</span>}
                        </h1>
                        <p className="text-[11px] text-muted-foreground">
                            {evolucao.length} dias · {motivos.length} motivos · {porFilial.length} filiais
                        </p>
                    </div>
                    <p className="text-[12px] text-muted-foreground">
                        {periodoSelecionado} · {filiaisSelecionadasLabel}
                        {baixandoImagem && dataGeracaoImagem && ` · Gerado em ${formatarDataHora(dataGeracaoImagem)}`}
                    </p>
                </div>

                {porCarga && (
                    <div className="rounded-xl border border-teal-500/30 bg-teal-500/5 px-4 py-3 text-xs text-muted-foreground">
                        <i className="fa fa-info-circle mr-1.5 text-teal-600" />
                        <strong className="text-foreground">Análise por Carga de Entrega:</strong> considera volume e retorno só dos
                        transportes que têm registro em Carga de Entrega (escala de rotas), com registros a partir de
                        {" "}<strong className="text-foreground">01/09/2026</strong>. O período é filtrado pela{" "}
                        <strong className="text-foreground">{campoData === "CRIACAO" ? "data de criação" : "data de remessa"}</strong> do volume e do retorno. O que vier antes dessa data não entra, e por isso o
                        comparativo com o ano anterior fica sem dados.
                        {relatorio.periodoInicial && relatorio.periodoInicial > filtroAplicado.dataInicial && (
                            <> Período consultado: <strong className="text-foreground">{formatLocalDate(relatorio.periodoInicial)}</strong> a{" "}
                                <strong className="text-foreground">{formatLocalDate(relatorio.periodoFinal)}</strong>.</>
                        )}
                    </div>
                )}

                {loading ? (
                    <Loading />
                ) : (
                    <div className="space-y-6">
                        {isEmpty ? (
                            <div className="flex flex-col items-center justify-center py-32 text-muted-foreground/50">
                                <i className="fa fa-bar-chart text-6xl mb-4" />
                                <p className="text-sm">Nenhum dado disponível.</p>
                            </div>
                        ) : (
                            <>
                                {/* ── KPIs ── */}
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                                    <KpiPorFilial label='YORK TOTAL' filial={consolidado} metrica={mk} />

                                    {porFilial.map((filial, index) => (
                                        <KpiPorFilial key={filial.id ?? filial.filial ?? index} filial={filial} metrica={mk} />
                                    ))}
                                </div>

                                {/* Toggle métrica global */}
                                {/* ── Seção 1: Evolução ── */}
                                <section className="space-y-4">

                                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                        <div className="xl:col-span-2">
                                            <ChartCard
                                                title={`Evolução Diária — Volume Entregue vs Retornado — ${mk.label}`}
                                                view={viewEvolucao}
                                                onViewChange={setViewEvolucao}
                                                accent={C.tealMid}
                                                tableContent={
                                                    <div className="overflow-x-auto max-h-[240px]">
                                                        <table className="w-full text-[11px] text-foreground">
                                                            <thead>
                                                                <tr className="border-b border-border text-muted-foreground font-semibold">
                                                                    <th className="text-left py-2 px-2">Data</th>
                                                                    <th className="text-right py-2 px-2">Entregues</th>
                                                                    <th className="text-right py-2 px-2">Retornados</th>
                                                                    <th className="text-right py-2 px-2">% Retorno</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {evolucao.map((e, i) => (
                                                                    <tr key={i} className="border-b border-border hover:bg-muted transition-colors">
                                                                        <td className="py-2 px-2 font-mono text-muted-foreground">{e.data}</td>
                                                                        <td className="py-2 px-2 text-right tabular-nums text-teal-700">{fmt(e[mk.ent])}</td>
                                                                        <td className="py-2 px-2 text-right tabular-nums text-rose-600">{fmt(e[mk.ret])}</td>
                                                                        <td className="py-2 px-2 text-right"><PctBadge value={e[mk.pct]} /></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                }
                                            >
                                                <Chart
                                                    type="area"
                                                    options={chartEvolucao.options}
                                                    series={chartEvolucao.series}
                                                    height={230}
                                                />
                                            </ChartCard>
                                        </div>
                                        <ChartCard
                                            title={`% Retorno ${mesesNoIntervalo > 1 ? "Mensal" : "Diário"} — ${mk.label}`}
                                            accent={C.amber}
                                            view="chart"
                                        >
                                            <Chart
                                                type="line"
                                                options={chartEvolucaoPct.options}
                                                series={chartEvolucaoPct.series}
                                                height={230}
                                            />
                                        </ChartCard>
                                    </div>
                                </section>

                                {/* ── Seção 2: Por Filial ── */}
                                {comparativoAno.length > 0 && (
                                    <section className="space-y-4">
                                        <ChartCard
                                            title={`Comparativo com Período Atual vs Período Anterior — ${mk.label} Efetivadas`}
                                            accent={C.violet}
                                            view={viewComparativo}
                                            onViewChange={setViewComparativo}
                                            tableContent={
                                                <div className="overflow-x-auto max-h-[300px]">
                                                    <table className="w-full text-[11px] text-foreground">
                                                        <thead>
                                                            <tr className="border-b border-border text-muted-foreground font-semibold">
                                                                <th className="text-left py-2 px-2">Mês/Ano</th>
                                                                <th className="text-right py-2 px-2">Período Atual</th>
                                                                <th className="text-right py-2 px-2">Período Anterior</th>
                                                                <th className="text-right py-2 px-2">Meta</th>
                                                                <th className="text-right py-2 px-2">% Crescimento</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {[...comparativoAno]
                                                                .sort((a, b) => (n(a.ano) - n(b.ano)) || (n(a.mes) - n(b.mes)))
                                                                .map((item, i) => (
                                                                    <tr key={i} className="border-b border-border hover:bg-muted transition-colors">
                                                                        <td className="py-2 px-2 font-semibold text-foreground">{`${MESES[n(item.mes) - 1] ?? item.mes}/${item.ano ?? ""}`}</td>
                                                                        <td className="py-2 px-2 text-right tabular-nums text-indigo-700">{fmt(item[mk.periodoAtual])}</td>
                                                                        <td className="py-2 px-2 text-right tabular-nums text-amber-600">{fmt(item[mk.periodoAnterior])}</td>
                                                                        <td className="py-2 px-2 text-right tabular-nums text-green-600">{item[mk.meta] != null ? fmt(item[mk.meta]) : "—"}</td>
                                                                        <td className="py-2 px-2 text-right">
                                                                            <span className={`inline-block px-2 py-0.5 rounded-full font-bold text-[10px] ${n(item[mk.pct]) >= 0 ? "bg-green-50 text-green-600" : "bg-rose-50 text-rose-600"
                                                                                }`}>
                                                                                {fmtP(item[mk.pct])}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            }
                                        >
                                            <Chart
                                                type="line"
                                                options={chartComparativoAno.options}
                                                series={chartComparativoAno.series}
                                                height={220}
                                            />
                                        </ChartCard>
                                    </section>
                                )}

                                <section id="consolidado-por-filial" className="space-y-4">
                                    {exibicao === "chart" && (
                                        <div className="grid grid-cols-1 xl:grid-cols-7 gap-4">
                                            <div className="xl:col-span-4">

                                                <ChartCard
                                                    title={`Consolidado por Filial — Entregue vs Retornado — ${mk.label}`}
                                                    accent={C.tealMid}
                                                    view="chart"
                                                    onViewChange={() => setExibicao('table')}
                                                >
                                                    <Chart
                                                        type="bar"
                                                        options={chartFilialBar.options}
                                                        series={chartFilialBar.series}
                                                        height={220}
                                                    />
                                                </ChartCard>
                                            </div>
                                            <div className="xl:col-span-3">

                                                <ChartCard
                                                    title={`% Retorno por Filial — ${mk.label}`}
                                                    accent={C.rose}
                                                    view="chart"
                                                    onViewChange={() => setExibicao('table')}
                                                >
                                                    <Chart
                                                        type="bar"
                                                        options={chartFilialPct.options}
                                                        series={chartFilialPct.series}
                                                        height={220}
                                                    />
                                                </ChartCard>
                                            </div>
                                        </div>
                                    )}
                                </section>

                                {/* ── Seção 3: Motivos ── */}
                                <section id="motivo-de-retorno" className="space-y-4">

                                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

                                        <ChartCard
                                            title={`Motivos de Retorno — Ranking de Motivos — ${mk.label}`}
                                            view="chart"
                                            accent={C.violet}
                                        >
                                            <Chart
                                                type="bar"
                                                options={chartMotivosBar.options}
                                                series={chartMotivosBar.series}
                                                height={320}
                                            />
                                        </ChartCard>
                                        <div className="col-span-2">
                                            <ChartCard
                                                title="Motivos de Retorno — Detalhamento de Motivos"
                                                view="chart"
                                                accent={C.rose}
                                            >
                                                <TabelaMotivos motivos={motivos} />
                                            </ChartCard>
                                        </div>
                                    </div>
                                </section>

                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                    <ChartCard
                                        title="Comparativo Cx.U. / Cx.F. / Cubos"
                                        accent={C.amber}
                                        view="chart" >
                                        <Chart
                                            type="bar"
                                            options={chartMotivosComparativo.options}
                                            series={chartMotivosComparativo.series}
                                            height={320}
                                        />
                                    </ChartCard>
                                    <ChartCard
                                        title={`Participação por Motivo — ${mk.label}`}
                                        accent={C.rose}
                                        view="chart"
                                    >
                                        <Chart
                                            type="donut"
                                            options={chartMotivosDonut.options}
                                            series={chartMotivosDonut.series}
                                            height={320}
                                        />
                                    </ChartCard>
                                </div>

                                {exibicao === "table" && (
                                    <ChartCard
                                        title="Detalhamento por Filial"
                                        view="table"
                                        accent={C.blue}
                                        onViewChange={() => setExibicao('chart')}
                                        tableContent={
                                            <TabelaFilial porFilial={porFilial} consolidado={consolidado} />

                                        }
                                    >
                                    </ChartCard>
                                )}

                                {/* ── Seção 4: Resumo % ── */}
                                <section className="space-y-4">
                                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                        <ChartCard
                                            title="% Retorno — Cx. Unit., Cx. Fís. e Cubos"
                                            accent={C.amber}
                                            view="chart" >
                                            <Chart
                                                type="radialBar"
                                                options={chartRadial.options}
                                                series={chartRadial.series}
                                                height={320}
                                            />
                                        </ChartCard>
                                        <div className="xl:col-span-2">
                                            <ChartCard
                                                title="Scorecard Consolidado"
                                                accent={C.violet}
                                                view="chart"  >
                                                <ScorecardConsolidado consolidado={consolidado} />
                                            </ChartCard>
                                        </div>
                                    </div>
                                </section>
                            </>
                        )}

                        <SecaoDownload
                            descricaoFiltro={descricaoFiltroDados}
                            baixando={baixandoPlanilha}
                            onBaixar={baixarPlanilha}
                        />

                        <SecaoComparativoTransportes
                            comparativo={comparativoTransportes}
                            carregando={carregandoComparativo}
                            descricaoFiltro={descricaoFiltroDados}
                            onAbrirTransporte={abrirTransporte}
                        />

                        <ConferenciaFechamentoSolar
                            parametros={parametrosFiltro}
                            descricaoFiltro={descricaoFiltroDados}
                        />

                        <ModalDetalheTransporte
                            linha={transporteAberto}
                            detalhe={detalheTransporte}
                            carregando={carregandoDetalhe}
                            onClose={() => setTransporteAberto(null)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
